import React, { useEffect, useState } from "react";
import Datatable from "../../EntryFile/datatable"; // Custom Datatable component
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { PlusIcon, DeleteIcon, EditIcon } from "../../EntryFile/imagePath";
import { Modal, Form } from "react-bootstrap";
import alertify from "alertifyjs";
import Tabletop from "../../EntryFile/tabletop";
import { X } from "lucide-react";
import { getEmployees, createEmployee, updateEmployee, deleteEmployee } from "../../core/api/employee";

const EmployeesCRUD = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [employeeForm, setEmployeeForm] = useState({
    firstName: "",
    surname: "",
    email: "",
    phone: "",
    isDoctor: true,
  });
  const [errors, setErrors] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState(null);

  // Fetch employees from the API
  const fetchData = async ({ page, pageSize }) => {
    setLoading(true);
    try {
      const result = await getEmployees({ page, limit: pageSize, search: search || "" });
      setData(result.data.employees);
      setTotalItems(result.data.totalEmployees);
    } catch (error) {
      console.error(error);
      alertify.error("Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search === null) return;
    fetchData({ page: 1, pageSize });
  }, [search]);

  // Handle form input changes
  const handleValueChange = (e) => {
    const { name, value } = e.target;
    setEmployeeForm((prevForm) => ({ ...prevForm, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  // Handle form submission (Add or Edit)
  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = ["firstName"];
    const newErrors = {};
    let hasErrors = false;

    requiredFields.forEach((field) => {
      if (!employeeForm[field]) {
        newErrors[field] = "This field is required";
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    try {
      if (editingEmployeeId) {
        await updateEmployee(editingEmployeeId, employeeForm);
        alertify.success("Employee updated successfully!");
      } else {
        await createEmployee(employeeForm);
        alertify.success("Employee added successfully!");
      }
      fetchData({ page: 1, pageSize }); // Refresh the employee list
      resetForm(); // Reset the form
      setShowAddModal(false);
      setShowEditModal(false);
    } catch (err) {
      alertify.error(err?.response?.data?.message || "Failed to save employee");
    }
  };

  // Reset the form
  const resetForm = () => {
    setEmployeeForm({
      firstName: "",
      surname: "",
      email: "",
      phone: "",
      isDoctor: true,
    });
    setErrors({});
    setEditingEmployeeId(null);
  };

  // Handle delete action
  const handleDeleteEmployee = async (employeeId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteEmployee(employeeId);
          Swal.fire("Deleted!", "The employee has been deleted.", "success");
          fetchData({ page: 1, pageSize }); // Refresh the list after deletion
        } catch (error) {
          alertify.error("Failed to delete employee");
        }
      }
    });
  };

  // Handle selected rows change
  const handleSelectedRows = (selectedRowKeys) => {
    setSelectedRows(selectedRowKeys);
  };

  useEffect(() => {
    fetchData({ page: 1, pageSize });
  }, []);

  // Define columns for the employees table
  const columns = [
    {
      title: "First Name",
      dataIndex: "firstName",
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
    },
    {
      title: "Surname",
      dataIndex: "surname",
      render:(key) => <span>{key || '-'}</span>,
      sorter: (a, b) => a.surname.localeCompare(b.surname),
    },
    {
      title: "Email",
      dataIndex: "email",
      render:(key) => <span>{key || '-'}</span>,
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      render:(key) => <span>{key || '-'}</span>,
      sorter: (a, b) => a.phone.localeCompare(b.phone),
    },
    {
      title: "Is Doctor",
      dataIndex: "isDoctor",
      render: (isDoctor) => (isDoctor ? "Yes" : "No"),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Actions",
      render: (record) => (
        <>
          <Link
            className="me-2"
            to="#"
            onClick={() => handleEditEmployee(record)}
          >
            <img src={EditIcon} alt="Edit" />
          </Link>
          <Link className="me-2" to="#" onClick={() => handleDeleteEmployee(record.id)}>
            <img src={DeleteIcon} alt="Delete" />
          </Link>
        </>
      ),
    },
  ];

  // Handle edit action
  const handleEditEmployee = (employee) => {
    setEmployeeForm({
      firstName: employee.firstName,
      surname: employee.surname,
      email: employee.email,
      phone: employee.phone,
      isDoctor: employee.isDoctor,
    });
    setEditingEmployeeId(employee.id);
    setShowEditModal(true);
  };

  // Close Add Employee Modal
  const closeAddModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  // Close Edit Employee Modal
  const closeEditModal = () => {
    setShowEditModal(false);
    resetForm();
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title">
              <h4>Employees List</h4>
              <h6>Manage your employees</h6>
            </div>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-added"
                onClick={() => setShowAddModal(true)}
              >
                <img src={PlusIcon} alt="img" className="me-1" />
                Add Employee
              </Link>
            </div>
          </div>

          {/* Employees Table */}
          <div className="card">
            <div className="card-body">
              <div className="row">
                <Tabletop
                  OnChange={(e) => setSearch(e.target.value)}
                  value={search}
                />
              </div>
              <div className="table-responsive">
                <Datatable
                  isLoading={loading}
                  columns={columns}
                  response={{
                    data: data?.map((item, index) => ({ ...item })),
                    totalDataCount: totalItems,
                  }}
                  fetchData={fetchData}
                  SelectedRows={handleSelectedRows}
                  NoPagination={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Employee Modal */}
      {
        showAddModal &&
        <Modal show={showAddModal} onHide={() => closeAddModal()} centered>
          <Modal.Header>
            <Modal.Title>Add Employee</Modal.Title>
            <button type="button" className="btn-close me-1" onClick={() => closeAddModal()}>
              <X size={20} />
            </button>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleEmployeeSubmit}>
              <Form.Group className="form-group">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={employeeForm.firstName}
                  onChange={handleValueChange}
                  className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                />
                {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  name="surname"
                  value={employeeForm.surname}
                  onChange={handleValueChange}
                  className={`form-control ${errors.surname ? "is-invalid" : ""}`}
                />
                {errors.surname && <div className="invalid-feedback">{errors.surname}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={employeeForm.email}
                  onChange={handleValueChange}
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  placeholder="+267 7xxxxxxx"
                  value={employeeForm.phone}
                  onChange={handleValueChange}
                  className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                />
                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Check
                  type="checkbox"
                  id="isDoctor"
                  name="isDoctor"
                  label="Is Doctor"
                  checked={employeeForm.isDoctor}
                  value={employeeForm.isDoctor}
                  onChange={
                    (e) => setEmployeeForm((prevForm) => ({ ...prevForm, isDoctor: e.target.checked }))
                  }
                  className={`${errors.isDoctor ? "is-invalid" : ""}`}
                />
                {errors.isDoctor && <div className="invalid-feedback">{errors.isDoctor}</div>}
              </Form.Group>



              <div className="d-flex justify-content-end mt-4">
                <button type="submit" className="btn btn-submit me-2" style={{ width: 140 }}>
                  Add Employee
                </button>
                <button type="button" className="btn btn-cancel" onClick={() => closeAddModal()}>
                  Cancel
                </button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      }

      {/* Edit Employee Modal */}
      {
        showEditModal &&
        <Modal show={showEditModal} onHide={() => closeEditModal()} centered>
          <Modal.Header>
            <Modal.Title>Edit Employee</Modal.Title>
            <button type="button" className="btn-close me-1" onClick={() => closeEditModal()}>
              <X size={20} />
            </button>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleEmployeeSubmit}>
              <Form.Group className="form-group">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={employeeForm.firstName}
                  onChange={handleValueChange}
                  className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                />
                {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  name="surname"
                  value={employeeForm.surname}
                  onChange={handleValueChange}
                  className={`form-control ${errors.surname ? "is-invalid" : ""}`}
                />
                {errors.surname && <div className="invalid-feedback">{errors.surname}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={employeeForm.email}
                  onChange={handleValueChange}
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  placeholder="+267 7xxxxxxx"
                  value={employeeForm.phone}
                  onChange={handleValueChange}
                  className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                />
                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Check
                  type="checkbox"
                  id="isDoctor"
                  name="isDoctor"
                  label="Is Doctor"
                  checked={employeeForm.isDoctor}
                  value={employeeForm.isDoctor}
                  onChange={
                    (e) => setEmployeeForm((prevForm) => ({ ...prevForm, isDoctor: e.target.checked }))
                  }
                  className={`${errors.isDoctor ? "is-invalid" : ""}`}
                />
                {errors.isDoctor && <div className="invalid-feedback">{errors.isDoctor}</div>}
              </Form.Group>

              <div className="d-flex justify-content-end mt-4">
                <button type="submit" className="btn btn-submit me-2" style={{ width: 140 }}>
                  Save Changes
                </button>
                <button type="button" className="btn btn-cancel" onClick={() => closeEditModal()}>
                  Cancel
                </button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      }
    </>
  );
};

export default EmployeesCRUD;